/* =====================================================================
 * Agent capacity limit domain helper
 *
 * The server refuses to start a turn when a concurrency ceiling is hit
 * and answers 429 with a structured reason. Each reason needs different
 * copy because each implies a different user action:
 *
 *   resource_busy → the *same document/session* is already running a
 *                   turn (usually another tab, or a teammate). Wait for
 *                   that one; retrying immediately will just fail again.
 *   user_limit    → this user has too many turns in flight. Their own
 *                   other turns will free up.
 *   total_limit   → the server is saturated. Nothing the user can do
 *                   except retry shortly.
 *
 * Shaped after `tokenExhaustion.ts` so both "server said no" paths look
 * and behave the same to the user.
 * ===================================================================== */

import type { CapacityLimitInfo } from '@auw/workspace-utils';
import i18n from '@auw/i18n';
import { useUIStore } from '../../uiStore';

/** Copy key suffix per reason; unknown reasons fall back to the generic one. */
function descriptionKey(reason: string): string {
  switch (reason) {
    case 'resource_busy':
      return 'common:error.capacityResourceBusy';
    case 'user_limit':
      return 'common:error.capacityUserLimit';
    case 'total_limit':
      return 'common:error.capacityTotalLimit';
    default:
      return 'common:error.capacityGeneric';
  }
}

export function handleCapacityLimited(info: CapacityLimitInfo): void {
  useUIStore.getState().addToast({
    type: 'warning',
    title: i18n.t('common:error.capacityLimited'),
    description: i18n.t(descriptionKey(info.reason), {
      limit: info.limit,
      retryAfter: info.retryAfterSec,
    }),
  });
}

/**
 * User-facing text for the assistant bubble.
 *
 * Kept separate from the toast so the message body explains what
 * happened after the toast is gone — a bubble reading only "error"
 * would leave the user with no idea their turn was never started.
 */
export function capacityLimitMessage(info: CapacityLimitInfo): string {
  return i18n.t(descriptionKey(info.reason), {
    limit: info.limit,
    retryAfter: info.retryAfterSec,
  });
}
